import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Power, PowerOff, X, Tag, MessageSquare, Link as LinkIcon } from "lucide-react";

const empty = { name: "", keywords: "", match_mode: "any", dm_text: "", link_url: "", cta_text: "", image_url: "", post_id: "" };

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [recentPostIds, setRecentPostIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | rule object
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: rulesData }, { data: postCounts }] = await Promise.all([
        api.get("/rules"),
        api.get("/analytics/post-comment-counts"),
      ]);
      setRules(rulesData);
      const ids = Object.keys(postCounts || {});
      setRecentPostIds(ids);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const open = (rule) => {
    if (rule === "new") {
      setForm(empty);
      setEditing("new");
    } else {
      setForm({
        name: rule.name,
        keywords: rule.keywords.join(", "),
        match_mode: rule.match_mode,
        dm_text: rule.dm_text,
        link_url: rule.link_url || "",
        cta_text: rule.cta_text || "",
        image_url: rule.image_url || "",
        post_id: rule.post_id || "",
      });
      setEditing(rule);
    }
  };

  const close = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      link_url: form.link_url || null,
      cta_text: form.cta_text || null,
      image_url: form.image_url || null,
      post_id: form.post_id || null,
    };
    try {
      if (editing === "new") {
        await api.post("/rules", payload);
        toast.success("Rule created");
      } else {
        await api.put(`/rules/${editing.id}`, payload);
        toast.success("Rule updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (rule) => {
    try {
      await api.post(`/rules/${rule.id}/toggle`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await api.delete(`/rules/${rule.id}`);
      toast.success("Rule deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="rules-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="chip bg-lavender">Automation</span>
          <h1 className="text-4xl md:text-5xl font-extrabold mt-3">Rules</h1>
          <p className="text-muted-foreground font-medium mt-1">Define keywords and the DM you want sent.</p>
        </div>
        <button onClick={() => open("new")} data-testid="new-rule-btn" className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary border-2 border-foreground rounded-md font-bold shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all text-sm">
          <Plus className="w-4 h-4" strokeWidth={2.5} /> New rule
        </button>
      </header>

      {loading ? (
        <div className="brutal p-6 inline-block">Loading…</div>
      ) : rules.length === 0 ? (
        <div className="brutal p-10 text-center bg-white" data-testid="rules-empty">
          <div className="w-14 h-14 mx-auto bg-mint border-2 border-foreground rounded-md shadow-brutal-sm flex items-center justify-center mb-4">
            <Tag className="w-7 h-7" strokeWidth={2.5} />
          </div>
          <h3 className="font-heading font-extrabold text-2xl">No rules yet</h3>
          <p className="text-muted-foreground font-medium mt-1 max-w-md mx-auto">Create your first rule to start sending DMs automatically when commenters use your keyword.</p>
          <button onClick={() => open("new")} className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary border-2 border-foreground rounded-md font-bold shadow-brutal-sm">
            <Plus className="w-4 h-4" strokeWidth={2.5} /> Create your first rule
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {rules.map((r) => (
            <div key={r.id} className={`brutal p-5 ${r.active ? "bg-white" : "bg-muted"}`} data-testid={`rule-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-heading font-extrabold text-xl">{r.name}</h3>
                  <span className={`chip mt-1 inline-block ${r.active ? "bg-mint" : "bg-muted"}`}>
                    {r.active ? "Active" : "Paused"}
                  </span>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => toggle(r)} title={r.active ? "Pause" : "Activate"} data-testid={`toggle-${r.id}`} className="p-2 border-2 border-foreground rounded-md bg-white hover:bg-accent transition-colors">
                    {r.active ? <PowerOff className="w-4 h-4" strokeWidth={2.5} /> : <Power className="w-4 h-4" strokeWidth={2.5} />}
                  </button>
                  <button onClick={() => open(r)} title="Edit" data-testid={`edit-${r.id}`} className="p-2 border-2 border-foreground rounded-md bg-white hover:bg-secondary transition-colors">
                    <Pencil className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                  <button onClick={() => remove(r)} title="Delete" data-testid={`delete-${r.id}`} className="p-2 border-2 border-foreground rounded-md bg-white hover:bg-destructive hover:text-white transition-colors">
                    <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
              <div className="space-y-2.5 text-sm">
                <div className="flex items-start gap-2">
                  <Tag className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5} />
                  <div className="flex flex-wrap gap-1.5">
                    {r.keywords.map((k) => (
                      <span key={k} className="chip bg-butter text-[10px]">{k}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MessageSquare className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span className="font-medium line-clamp-2">{r.dm_text}</span>
                </div>
                {r.link_url && (
                  <div className="flex items-start gap-2">
                    <LinkIcon className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5} />
                    <span className="font-medium text-muted-foreground truncate">{r.link_url}</span>
                  </div>
                )}
                <div className="text-xs text-muted-foreground font-semibold pt-1">
                  Triggered <strong className="text-foreground">{r.trigger_count || 0}</strong> times
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={close}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="brutal bg-white p-6 max-w-lg w-full max-h-[calc(100vh-4rem)] overflow-y-auto space-y-4 my-8">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading font-extrabold text-2xl">{editing === "new" ? "New rule" : "Edit rule"}</h3>
                <p className="text-sm text-muted-foreground font-medium">When a comment matches, send the DM below.</p>
              </div>
              <button type="button" onClick={close} className="p-1.5 border-2 border-foreground rounded-md"><X className="w-4 h-4" /></button>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Rule name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rule-name" className="brutal-input w-full" placeholder="Free Guide DM" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Keywords (comma-separated)</label>
              <input required value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} data-testid="rule-keywords" className="brutal-input w-full" placeholder="LINK, GUIDE, FREEBIE" />
              <div className="text-xs text-muted-foreground mt-1 font-medium">Case-insensitive substring match.</div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Match mode</label>
              <div className="flex gap-2">
                {[
                  { v: "any", label: "Any keyword" },
                  { v: "all", label: "All keywords" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    data-testid={`match-${o.v}`}
                    onClick={() => setForm({ ...form, match_mode: o.v })}
                    className={`flex-1 py-2 border-2 border-foreground rounded-md text-sm font-bold ${form.match_mode === o.v ? "bg-primary shadow-brutal-sm" : "bg-white"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">DM message</label>
              <textarea required value={form.dm_text} onChange={(e) => setForm({ ...form, dm_text: e.target.value })} data-testid="rule-dm-text" className="brutal-input w-full min-h-[100px]" placeholder="Hey! Here's the free guide I promised 👇" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Link URL</label>
                <input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} data-testid="rule-link" className="brutal-input w-full" placeholder="https://..." />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">CTA button text</label>
                <input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} data-testid="rule-cta" className="brutal-input w-full" placeholder="Get it now" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Restrict to post link or ID (optional)</label>
              <input
                value={form.post_id}
                onChange={(e) => setForm({ ...form, post_id: e.target.value })}
                data-testid="rule-post-id"
                className="brutal-input w-full"
                placeholder="Leave empty for all posts"
                list="known-post-ids"
              />
              <datalist id="known-post-ids">
                {recentPostIds.map((id) => (
                  <option key={id} value={id} />
                ))}
              </datalist>
              <div className="text-xs text-muted-foreground mt-1 font-medium">
                Tip: paste a Facebook post link, or pick a recent post ID below.
              </div>
              {recentPostIds.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {recentPostIds.slice(0, 4).map((id) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setForm({ ...form, post_id: id })}
                      className="chip bg-white border-2 border-foreground text-[11px] hover:bg-secondary"
                    >
                      Use {id}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={close} className="flex-1 py-2.5 border-2 border-foreground rounded-md font-bold">Cancel</button>
              <button type="submit" disabled={busy} data-testid="rule-save" className="flex-1 py-2.5 bg-primary border-2 border-foreground rounded-md font-bold shadow-brutal-sm disabled:opacity-50">{busy ? "Saving…" : "Save rule"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
